<template>
  <div class="m-edition-billing-artificial-intelligence-costs">
    <div v-if="cargando" class="ia-costos-cargando">
      <i class="fas fa-circle-notch fa-spin"></i> Cargando…
    </div>

    <template v-else>
      <!--
        Los ajustes van ARRIBA de la conciliación: lo que se viene a tocar con apuro es un
        interruptor, y los números del mes se miran con tiempo.
      -->
      <MoleculeEditionBillingArtificialIntelligenceSettings />

      <div v-if="aviso" class="ia-costos-aviso">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ aviso }}</span>
      </div>

      <!--
        De las dos mitades del costo medimos una sola: los tokens vienen en cada respuesta del
        proveedor y son exactos, pero lo que vale un token no lo informa nadie. Por eso esta pantalla
        tiene dos partes: arriba se carga lo único que no se puede medir (la factura del mes) y abajo
        se mantiene el precio de cada modelo, que es de donde sale el costo calculado.
      -->
      <section class="ia-costos-bloque">
        <header class="ia-costos-head">
          <div>
            <h3>Conciliación con el proveedor</h3>
            <p>
              Los tokens de cada mes contra lo que el proveedor facturó de verdad. Hasta que no
              cargues la factura, el margen es un cálculo nuestro y no una prueba.
            </p>
          </div>
          <span class="ia-costos-objetivo">
            Margen objetivo <strong>×{{ margenObjetivo }}</strong>
          </span>
        </header>

        <div class="ia-costos-tabla-marco">
          <table class="ia-costos-tabla">
            <thead>
              <tr>
                <th>Mes</th>
                <th class="num">Tokens</th>
                <th class="num">Créditos cobrados</th>
                <th class="num">Ingreso</th>
                <th class="num">Costo calculado</th>
                <th class="num">Facturado</th>
                <th class="num">Margen real</th>
                <th class="num">Desvío</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fila in conciliacion" :key="fila.proveedor + fila.periodo">
                <td>
                  <strong>{{ mes(fila.periodo) }}</strong>
                  <small class="ia-costos-proveedor">{{ fila.proveedor }}</small>
                </td>
                <td class="num">{{ miles(fila.tokens) }}</td>
                <td class="num">{{ miles(fila.creditos_cobrados) }}</td>
                <td class="num">{{ usd(fila.ingreso_usd) }}</td>
                <td class="num">{{ usd(fila.costo_calculado_usd) }}</td>
                <td class="num">
                  <!--
                    Editable en la misma fila: cargar la factura es una tarea de un minuto una vez
                    por mes, y sacarla a un modal la vuelve una excursión.
                  -->
                  <div class="ia-costos-factura">
                    <input
                      v-model="facturas[claveDe(fila)]"
                      type="number"
                      step="0.0001"
                      min="0"
                      placeholder="—"
                      @keyup.enter="guardarFactura(fila)"
                    />
                    <button
                      type="button"
                      :disabled="guardandoFactura === claveDe(fila)"
                      @click="guardarFactura(fila)"
                    >
                      <i
                        :class="guardandoFactura === claveDe(fila)
                          ? 'fas fa-circle-notch fa-spin'
                          : 'fas fa-check'"
                      ></i>
                    </button>
                  </div>
                </td>
                <td class="num">
                  <span
                    v-if="fila.margen_real !== null"
                    class="ia-costos-margen"
                    :class="claseMargen(fila.margen_real)"
                  >×{{ fila.margen_real }}</span>
                  <span v-else class="ia-costos-sin-dato" title="Falta cargar la factura del mes">
                    ×{{ fila.margen_calculado === null ? '—' : fila.margen_calculado }}
                    <i class="fas fa-info-circle"></i>
                  </span>
                </td>
                <td class="num">
                  <span v-if="fila.desvio === null" class="ia-costos-sin-dato">—</span>
                  <span v-else :class="fila.desvio > 5 ? 'ia-costos-desvio-alto' : ''">
                    {{ fila.desvio > 0 ? '+' : '' }}{{ fila.desvio }}%
                  </span>
                </td>
              </tr>
              <tr v-if="conciliacion.length === 0">
                <td colspan="8" class="ia-costos-vacio">
                  Todavía no hay generaciones registradas en este período.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="ia-costos-pie">
          El <strong>margen real</strong> es lo que cobraste dividido por lo que pagaste. Si da
          ×{{ margenObjetivo }}, la tarifa está bien. El <strong>desvío</strong> compara la factura
          con nuestro cálculo: positivo quiere decir que el proveedor cobró más de lo que creíamos, y
          es la señal de que hay que actualizar los precios de abajo.
        </p>
      </section>

      <section class="ia-costos-bloque">
        <header class="ia-costos-head">
          <div>
            <h3>Precio de cada modelo</h3>
            <p>
              Es la mitad del costo que no se puede medir. Lo que cargues acá manda sobre el
              <code>.env</code> y vale desde la próxima generación, sin desplegar nada.
            </p>
          </div>
        </header>

        <div class="ia-costos-tabla-marco">
          <table class="ia-costos-tabla">
            <thead>
              <tr>
                <th>Modelo</th>
                <th>Cobro</th>
                <th class="num">Entrada (USD/M tokens)</th>
                <th class="num">Salida (USD/M tokens)</th>
                <th class="num">Por imagen (USD)</th>
                <th>Origen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="precio in precios" :key="precio.proveedor + precio.modelo">
                <td>
                  <strong>{{ precio.modelo }}</strong>
                  <small class="ia-costos-proveedor">{{ precio.proveedor }}</small>
                </td>
                <td>{{ precio.cobro === 'imagen' ? 'Por imagen' : 'Por token' }}</td>
                <td class="num">
                  <input
                    v-if="precio.cobro === 'token'"
                    v-model="edicion[claveModelo(precio)].input_mtok"
                    type="number"
                    step="0.000001"
                    min="0"
                  />
                  <span v-else class="ia-costos-sin-dato">—</span>
                </td>
                <td class="num">
                  <input
                    v-if="precio.cobro === 'token'"
                    v-model="edicion[claveModelo(precio)].output_mtok"
                    type="number"
                    step="0.000001"
                    min="0"
                  />
                  <span v-else class="ia-costos-sin-dato">—</span>
                </td>
                <td class="num">
                  <input
                    v-if="precio.cobro === 'imagen'"
                    v-model="edicion[claveModelo(precio)].imagen"
                    type="number"
                    step="0.000001"
                    min="0"
                  />
                  <span v-else class="ia-costos-sin-dato">—</span>
                </td>
                <td>
                  <span class="ia-costos-origen" :class="'origen-' + precio.origen">
                    {{ precio.origen === 'base' ? 'Cargado' : 'Del .env' }}
                  </span>
                  <small v-if="!precio.en_uso" class="ia-costos-sin-uso">sin uso</small>
                </td>
                <td class="ia-costos-acciones">
                  <button
                    type="button"
                    :disabled="guardandoPrecio === claveModelo(precio)"
                    @click="guardarPrecio(precio)"
                  >
                    <i
                      :class="guardandoPrecio === claveModelo(precio)
                        ? 'fas fa-circle-notch fa-spin'
                        : 'fas fa-check'"
                    ></i>
                    Guardar
                  </button>
                  <!--
                    Borrar no deja al modelo sin precio: lo devuelve al del .env. Es la salida si
                    alguien carga un número equivocado.
                  -->
                  <button
                    v-if="precio.origen === 'base'"
                    type="button"
                    class="ia-costos-restaurar"
                    @click="restaurarPrecio(precio)"
                  >
                    <i class="fas fa-undo"></i> Volver al .env
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * Costos y margen de la IA (solo superadministrador de Vidroop).
 *
 * Responde la única pregunta que decide la tarifa: ¿cuánto cobramos y cuánto pagamos? Los tokens de
 * cada generación quedan registrados y son exactos; lo que no se puede medir es el dinero, así que
 * la factura del proveedor se carga a mano una vez por mes y con eso sale el margen REAL.
 *
 * La segunda tabla es la otra mitad: el precio de cada modelo, que hasta ahora vivía solo en el
 * `.env` y cambiarlo pedía un despliegue. Google duplica los precios de Gemini el 01-01-2027; con
 * esto se actualizan desde acá y la próxima generación ya cobra bien.
 */
import MoleculeEditionBillingArtificialIntelligenceSettings from './MoleculeEditionBillingArtificialIntelligenceSettings'

export default {
  name: 'MoleculeEditionBillingArtificialIntelligenceCosts',

  components: { MoleculeEditionBillingArtificialIntelligenceSettings },

  data () {
    return {
      aviso: '',
      cargando: true,
      conciliacion: [],
      // El importe tipeado de cada mes, indexado por proveedor|periodo
      facturas: {},
      guardandoFactura: '',
      guardandoPrecio: '',
      margenObjetivo: 3,
      precios: [],
      // Lo tipeado en la tabla de precios, indexado por proveedor|modelo
      edicion: {}
    }
  },

  created () {
    this.cargar()
  },

  methods: {
    claveDe (fila) {
      return `${fila.proveedor}|${fila.periodo}`
    },

    claveModelo (precio) {
      return `${precio.proveedor}|${precio.modelo}`
    },

    claseMargen (margen) {
      if (margen >= this.margenObjetivo) return 'margen-bien'
      if (margen >= this.margenObjetivo * 0.75) return 'margen-justo'
      return 'margen-mal'
    },

    mes (periodo) {
      const [anio, mes] = String(periodo).split('-')
      const nombres = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      return `${nombres[Number(mes) - 1] || mes} ${anio}`
    },

    miles (valor) {
      return Number(valor || 0).toLocaleString('es-AR')
    },

    usd (valor) {
      if (valor === null || valor === undefined) return '—'
      return `US$ ${Number(valor).toFixed(valor < 1 ? 4 : 2)}`
    },

    cargar () {
      this.cargando = true

      this.$services.read('sateliteIaMonitoreo', 'readMonitoreoIaConciliacion')
        .then(({ data }) => {
          this.aviso = data.aviso || ''
          this.margenObjetivo = data.margen_objetivo || 3
          this.conciliacion = data.conciliacion || []
          this.aplicarPrecios(data.precios || [])

          this.facturas = {}
          this.conciliacion.forEach(fila => {
            this.$set(this.facturas, this.claveDe(fila), fila.facturado_usd)
          })
        })
        .catch(() => {
          this.aviso = 'No se pudo leer la conciliación. Vuelve a intentarlo.'
        })
        .finally(() => {
          this.cargando = false
        })
    },

    /**
     * Guarda los precios y prepara lo editable. Se llama al cargar y después de cada guardado,
     * porque el origen de cada fila (cargado o del .env) cambia con un alta o un borrado.
     */
    aplicarPrecios (precios) {
      this.precios = precios
      this.edicion = {}

      precios.forEach(precio => {
        this.$set(this.edicion, this.claveModelo(precio), {
          input_mtok: precio.input_mtok,
          output_mtok: precio.output_mtok,
          imagen: precio.imagen
        })
      })
    },

    guardarFactura (fila) {
      const clave = this.claveDe(fila)
      const importe = this.facturas[clave]

      if (importe === null || importe === undefined || importe === '') return

      this.guardandoFactura = clave

      this.$services.update('sateliteIaMonitoreo', 'updateMonitoreoIaFactura', {
        proveedor: fila.proveedor,
        // El backend espera el mes, no una fecha
        periodo: String(fila.periodo).slice(0, 7),
        importe_usd: Number(importe)
      })
        .then(() => {
          this.$alerts.pushAlertNotTranslated(
            `Factura de ${this.mes(fila.periodo)} guardada.`,
            'a-alert-success'
          )
          // Se relee: el margen y el desvío del mes se recalculan con el importe nuevo
          this.cargar()
        })
        .catch(() => {
          this.$alerts.pushAlertNotTranslated('No se pudo guardar la factura.', 'a-alert-danger')
        })
        .finally(() => {
          this.guardandoFactura = ''
        })
    },

    guardarPrecio (precio) {
      const clave = this.claveModelo(precio)
      const valores = this.edicion[clave] || {}

      this.guardandoPrecio = clave

      this.$services.update('sateliteIaMonitoreo', 'updateMonitoreoIaPrecio', {
        proveedor: precio.proveedor,
        modelo: precio.modelo,
        input_mtok: this.comoNumero(valores.input_mtok),
        output_mtok: this.comoNumero(valores.output_mtok),
        imagen: this.comoNumero(valores.imagen)
      })
        .then(({ data }) => {
          this.aplicarPrecios(data.precios || [])
          this.$alerts.pushAlertNotTranslated(
            `Precio de ${precio.modelo} guardado.`,
            'a-alert-success'
          )
        })
        .catch(() => {
          this.$alerts.pushAlertNotTranslated('No se pudo guardar el precio.', 'a-alert-danger')
        })
        .finally(() => {
          this.guardandoPrecio = ''
        })
    },

    restaurarPrecio (precio) {
      this.guardandoPrecio = this.claveModelo(precio)

      this.$services.update('sateliteIaMonitoreo', 'updateMonitoreoIaPrecio', {
        proveedor: precio.proveedor,
        modelo: precio.modelo,
        borrar: true
      })
        .then(({ data }) => {
          this.aplicarPrecios(data.precios || [])
          this.$alerts.pushAlertNotTranslated(
            `${precio.modelo} vuelve al precio del .env.`,
            'a-alert-success'
          )
        })
        .catch(() => {
          this.$alerts.pushAlertNotTranslated('No se pudo restaurar el precio.', 'a-alert-danger')
        })
        .finally(() => {
          this.guardandoPrecio = ''
        })
    },

    /**
     * Un vacío es null y no 0: null quiere decir "este modelo no se cobra así", y un 0 sería un
     * modelo gratuito. La diferencia importa porque media fila se ignora al cobrar.
     */
    comoNumero (valor) {
      if (valor === null || valor === undefined || valor === '') return null
      return Number(valor)
    }
  }
}
</script>
